import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CustomerProvider } from './context/CustomerContext';
import { ThemeProvider } from './components/ThemeToggle';
import LandingPage from './pages/LandingPage';
import SmartMarketing from './components/SmartMarketing';
import LPEProPage from './pages/LPEProPage';
import LocalCityPage from './pages/LocalCityPage';
import VendorLoginPage from './pages/VendorLoginPage';
import AdminPageV2 from './pages/AdminPageV2';
import SearchPage from './pages/SearchPage';
import CustomerDashboard from './pages/CustomerDashboard';
import HistoryPage from './pages/HistoryPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientInstructionsPage from './pages/ClientInstructionsPage';
import LivraisonPage from './pages/LivraisonPage';
import OffreBienvenuePage from './pages/OffreBienvenuePage';
import ConfirmerEmailPage from './pages/ConfirmerEmailPage';
import VerifyCustomerEmailPage from './pages/VerifyCustomerEmailPage';
import DesinscrirePage from './pages/DesinscrirePage';
import MerciPage from './pages/MerciPage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import DriverPageV2 from './pages/DriverPageV2';
import RencontresLanding from './pages/RencontresLanding';
import SupremeAdminPage from './pages/SupremeAdminPage';
import SmartLanding from './components/SmartLanding';
import SmartLegalPage from './components/SmartLegalPage';
import ProtectedRoute from './components/ProtectedRoute';
import { OfflineStatusBadge } from './components/OfflineStatusBar';
import { Toaster } from './components/ui/sonner';
import MaintenanceMode, { isMaintenanceMode } from './components/MaintenanceMode';
import { useVisitorTracking } from './hooks/useVisitorTracking';
import { PixieGlobal } from './components/PixieGlobal';
// 💜 Heartbeats.fr Standalone Pages
import HeartbeatsAuth from './pages/HeartbeatsAuth';
import HeartbeatsPricing from './pages/HeartbeatsPricing';
import HeartbeatsLanding from './pages/HeartbeatsLanding';
import HeartbeatsPortal from './pages/HeartbeatsPortal';
import MarketingPage from './pages/MarketingPage';
import AffiliateRedirectPage from './pages/AffiliateRedirectPage';
// 🏙️ SEO City Pages
import SeoCityPage from './pages/SeoCityPage';
// 📜 Heartbeats Legal Pages
import HeartbeatsMentionsLegales from './pages/HeartbeatsMentionsLegales';
import HeartbeatsCGU from './pages/HeartbeatsCGU';
import HeartbeatsConfidentialite from './pages/HeartbeatsConfidentialite';
// 💳 Payment Pages
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
// 📜 Pages légales
import CGUPage from './pages/CGUPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import './i18n'; // 🌍 Initialisation i18n
import './App.css';

// Composant wrapper pour le tracking
const TrackingWrapper = ({ children }) => {
  // Récupérer le customerId depuis le localStorage si connecté
  const customerData = localStorage.getItem('customer');
  const customerId = customerData ? JSON.parse(customerData)?.id : null;
  
  useVisitorTracking(customerId);
  return children;
};

// 🚀 Composant de redirection pour Supreme Admin
// Redirige vers heartbeats.fr si on est sur LPE
const SupremeAdminRedirect = () => {
  const hostname = window.location.hostname.toLowerCase();
  
  // Si on est sur heartbeats.fr (production), afficher la page
  const isHeartbeatsDomain = 
    hostname === 'heartbeats.fr' || 
    hostname === 'www.heartbeats.fr' ||
    hostname.endsWith('.heartbeats.fr');
  
  // Si on est en preview/localhost, afficher aussi (pour les tests)
  const isPreviewOrLocal = 
    hostname.includes('preview') || 
    hostname.includes('emergentagent') ||
    hostname.includes('localhost');
  
  if (isHeartbeatsDomain || isPreviewOrLocal) {
    return <SupremeAdminPage />;
  }
  
  // Sinon (LPE production), rediriger vers heartbeats.fr
  window.location.href = 'https://heartbeats.fr/supreme-admin';
  return null;
};

function App() {
  // 🚨 DÉSACTIVER le Service Worker - il causait des erreurs "Body already consumed"
  // sur les requêtes de paiement Stripe et autres appels API POST
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Désenregistrer TOUS les service workers existants pour éviter les problèmes de cache
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
          console.log('Service Worker désenregistré:', registration.scope);
        }
      });
    }
  }, []);

  // Vérifier le mode maintenance
  if (isMaintenanceMode()) {
    return <MaintenanceMode />;
  }

  return (
    <HelmetProvider>
    <ThemeProvider>
      <CustomerProvider>
        <BrowserRouter>
          <TrackingWrapper>
            <div className="App">
              <Routes>
              {/* Routes CLIENT */}
              {/* SmartLanding détecte automatiquement le domaine */}
              {/* la-petite-epicerie.shop → LandingPage */}
              {/* heartbeats.fr → Redirige vers /client (mode maintenance géré dans App.js) */}
              <Route path="/" element={<SmartLanding />} />
              <Route path="/lpe" element={<LandingPage />} />
              <Route path="/marketing" element={<SmartMarketing />} />
              <Route path="/pro" element={<LPEProPage />} />
              
              {/* 💜 Heartbeats Landing - Accessible via /heartbeats-preview en preview */}
              <Route path="/heartbeats-preview" element={<HeartbeatsLanding />} />
              
              {/* Routes SEO Local - Épicerie par ville */}
              <Route path="/epicerie/:city" element={<LocalCityPage />} />
              
              <Route path="/client" element={<ClientPortalPage />} />
              <Route path="/client/auth-callback" element={<GoogleAuthCallback />} />
              <Route path="/livraison" element={<LivraisonPage />} />
              <Route path="/offre-bienvenue" element={<OffreBienvenuePage />} />
              <Route path="/merci" element={<MerciPage />} />
              <Route path="/confirmer-email" element={<ConfirmerEmailPage />} />
              <Route path="/confirmer-email/:token" element={<ConfirmerEmailPage />} />
              <Route path="/verify-customer-email" element={<VerifyCustomerEmailPage />} />
              <Route path="/desinscrire" element={<DesinscrirePage />} />
              <Route path="/instructions" element={<ClientInstructionsPage />} />
              
              {/* 💜 Landing Heartbeats - Capture célibataires */}
              <Route path="/rencontres" element={<RencontresLanding />} />
              
              {/* 🔗 Tracking affilié - Liens de parrainage */}
              <Route path="/r/:affiliateCode" element={<AffiliateRedirectPage />} />
              
              {/* 🏙️ SEO City Pages - 20 principales villes de France */}
              <Route path="/rencontre-paris" element={<SeoCityPage citySlug="paris" />} />
              <Route path="/rencontre-marseille" element={<SeoCityPage citySlug="marseille" />} />
              <Route path="/rencontre-lyon" element={<SeoCityPage citySlug="lyon" />} />
              <Route path="/rencontre-bordeaux" element={<SeoCityPage citySlug="bordeaux" />} />
              <Route path="/rencontre-lille" element={<SeoCityPage citySlug="lille" />} />
              <Route path="/rencontre-toulouse" element={<SeoCityPage citySlug="toulouse" />} />
              <Route path="/rencontre-nice" element={<SeoCityPage citySlug="nice" />} />
              <Route path="/rencontre-nantes" element={<SeoCityPage citySlug="nantes" />} />
              <Route path="/rencontre-strasbourg" element={<SeoCityPage citySlug="strasbourg" />} />
              <Route path="/rencontre-montpellier" element={<SeoCityPage citySlug="montpellier" />} />
              <Route path="/rencontre-rennes" element={<SeoCityPage citySlug="rennes" />} />
              <Route path="/rencontre-reims" element={<SeoCityPage citySlug="reims" />} />
              <Route path="/rencontre-le-havre" element={<SeoCityPage citySlug="le-havre" />} />
              <Route path="/rencontre-saint-etienne" element={<SeoCityPage citySlug="saint-etienne" />} />
              <Route path="/rencontre-toulon" element={<SeoCityPage citySlug="toulon" />} />
              <Route path="/rencontre-grenoble" element={<SeoCityPage citySlug="grenoble" />} />
              <Route path="/rencontre-dijon" element={<SeoCityPage citySlug="dijon" />} />
              <Route path="/rencontre-angers" element={<SeoCityPage citySlug="angers" />} />
              <Route path="/rencontre-nimes" element={<SeoCityPage citySlug="nimes" />} />
              <Route path="/rencontre-villeurbanne" element={<SeoCityPage citySlug="villeurbanne" />} />
              
              {/* 💜 HEARTBEATS.FR - Routes standalone */}
              <Route path="/heartbeats-portal" element={<HeartbeatsPortal />} />
              <Route path="/auth" element={<HeartbeatsAuth 
                onSuccess={(data) => {
                  if (data.token) localStorage.setItem('heartbeats_token', data.token);
                  if (data.user) localStorage.setItem('heartbeats_user', JSON.stringify(data.user));
                  // Redirection vers le portail Heartbeats dédié
                  window.location.href = '/heartbeats-portal';
                }}
                onBack={() => window.location.href = '/'}
              />} />
              <Route path="/pricing" element={<HeartbeatsPricing 
                onSelectPlan={(planId) => console.log('Plan selected:', planId)}
                onBack={() => window.location.href = '/'}
              />} />
              {/* 🚀 Page de vente SaaS - Système de fidélisation */}
              <Route path="/business" element={<MarketingPage />} />
              <Route path="/saas" element={<MarketingPage />} />
              
              {/* 📜 Pages légales - Détection automatique du domaine */}
              <Route path="/cgu" element={<SmartLegalPage type="cgu" />} />
              <Route path="/privacy" element={<SmartLegalPage type="privacy" />} />
              <Route path="/confidentialite" element={<SmartLegalPage type="privacy" />} />
              <Route path="/mentions-legales" element={<SmartLegalPage type="mentions" />} />
              
              {/* 💳 Pages de paiement */}
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              
              {/* Route LIVREUR */}
              <Route path="/livreur" element={<DriverPageV2 />} />
              <Route path="/livreurs" element={<DriverPageV2 />} />
              
              {/* Routes TEAM LPE */}
              <Route path="/team-lpe" element={<VendorLoginPage />} />
              <Route path="/team-lpe/admin" element={<AdminPageV2 />} />
              <Route path="/team-lpe/admin-legacy" element={<Navigate to="/team-lpe/admin" replace />} />
              <Route path="/team-lpe/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
              <Route path="/team-lpe/customer/:customerId" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
              <Route path="/team-lpe/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
              
              {/* 🔮 MEGA ADMIN SUPREME - Dashboard propriétaire SaaS */}
              {/* 🚀 Accessible UNIQUEMENT sur heartbeats.fr - Redirige depuis LPE */}
              <Route path="/supreme-admin" element={<SupremeAdminRedirect />} />
              
              {/* Anciennes routes - redirections */}
              <Route path="/vendor-login" element={<Navigate to="/team-lpe" replace />} />
              <Route path="/admin" element={<Navigate to="/team-lpe/admin" replace />} />
              <Route path="/search" element={<Navigate to="/team-lpe/search" replace />} />
              <Route path="/customer/:customerId" element={<Navigate to="/team-lpe/search" replace />} />
              <Route path="/history" element={<Navigate to="/team-lpe/history" replace />} />
            </Routes>
            <OfflineStatusBadge />
            <Toaster position="top-right" richColors />
            {/* 🧚‍♀️ Pixie Global - DÉSACTIVÉ TEMPORAIREMENT */}
            {/* <PixieGlobal delay={6000} position="bottom-right" /> */}
          </div>
        </TrackingWrapper>
      </BrowserRouter>
    </CustomerProvider>
  </ThemeProvider>
  </HelmetProvider>
  );
}

export default App;